/** Market and kitchen garden: furnish the edges, reserve circulation first.
 * Stable component streams vary stock/crops without rerolling paths or shelter. */
import type {AssetVec3} from '../proceduralAssets.js';
import type {FrontierBuilder} from './frontierExtras.js';
import type {FrontierPlacePlan} from './frontierTown.js';
import type {GrowingBedPlan,WorkplacePlan} from './workplace.js';
import {counterStand,counterTop} from './counterContact.js';
import {rngFromString} from '../rng.js';

/** Shallow wooden tray with produce resting inside its rim. */
function produceTray(b:FrontierBuilder,x:number,y:number,z:number,material:number){
  b.box([.68,.07,.64],[x,y,z],12);
  for(const dx of [-.32,.32])b.box([.055,.19,.64],[x+dx,y+.08,z],12);
  for(const dz of [-.29,.29])b.box([.64,.19,.055],[x,y+.08,z+dz],12);
  for(const dz of [-.14,.14]){
    const fruit=b.node('sphere',[.13],[x-.16,y+.16,z+dz],material);
    fruit.modifiers=[{type:'array',mode:'linear',count:2,offset:[.3,0,0]}];
  }
}
function pot(b:FrontierBuilder,x:number,y:number,z:number){
  b.node('cylinder',[.18,.13,.3],[x,y+.15,z],2);
  // Dark inset + thick lip read as an open vessel, rather than a solid drum.
  b.node('cylinder',[.19,.19,.055],[x,y+.31,z],3);
  b.node('cylinder',[.14,.14,.01],[x,y+.338,z],14);
}

export function frontierMarket(b:FrontierBuilder,d:FrontierPlacePlan['design'],seed:string):WorkplacePlan{
  const {part,box,node,floor,awning,post,slot,barrel}=b;
  floor(9,6);
  const bayW=8.4/d.marketBays,centers=Array.from({length:d.marketBays},(_,i)=>(i-(d.marketBays-1)/2)*bayW);
  const counterDepth=.85,counterZ=1.1;
  const entranceX=centers[0],vendorX=centers[1],scaleX=vendorX+(bayW-.4)/2-.4;
  part('market:shared-shelter',()=>{
    for(const [i,x] of centers.entries())awning(`stall-${i}`,x,bayW,-2.6,2.6,d.marketHeight,d.marketHeight,'frame',.65,i%2?3:6);
    box([8.4,d.marketHeight-.3,.18],[0,(d.marketHeight+.3)/2,-2.6],0,true);
    for(const y of [.55,d.marketHeight-.18])box([8.5,.16,.24],[0,y,-2.59],1);
    // High braces tie the open sides into the same frame as the cloth.
    for(const x of [-4.2,4.2])for(const z of [-2.3,2.3])box([.12,.7,.12],[x,d.marketHeight-.45,z],1,false,[z>0?-.65:.65,0,0]);
  });
  part('market:entry-runner',()=>{
    box([bayW-.9,.015,3.8],[entranceX,.309,.65],5);
    for(const x of [entranceX-(bayW-.9)/2+.12,entranceX+(bayW-.9)/2-.12])box([.05,.018,3.8],[x,.31,.65],6);
  });
  for(let bay=1;bay<centers.length;bay++)part(`market:counter-${bay}`,()=>{
    const x=centers[bay],width=bayW-.4;
    if(bay===1)counterTop(b,[x,1.215,counterZ],[width,.17,counterDepth],12,-1);
    else box([width,.17,counterDepth],[x,1.215,counterZ],12,true);
    box([width,.72,.12],[x,.77,1.43],1,true);
    for(const xx of [x-width/2+.16,x+width/2-.16])post(xx,1.1,.75,.3);
    const rng=rngFromString(`${seed}:market:counter:${bay}`),count=Math.floor(width/.85);
    for(let i=0;i<count;i++){
      const xx=x+(i-(count-1)/2)*.83;
      if(bay===1&&Math.abs(xx-scaleX)<.65)continue;
      if(bay===2&&rng()>.45){pot(b,xx,1.3,1.1);pot(b,xx+.22,1.3,1.25);}
      else produceTray(b,xx,1.335,1.1,rng()<.5?9:2);
    }
    box([.64,.3,.06],[x,.9,1.52],5);
    for(const dx of [-.16,0,.16])box([.07,.06,.02],[x+dx,.91,1.558],6);
  });
  part('market:side-display',()=>{
    // First bay is the shopping entrance, so its display runs along the side.
    box([.64,.14,2.5],[-3.72,1.17,-.1],12,true);
    for(const z of [-1.1,.9])post(-3.72,z,.8,.3);
    for(const z of [-.9,0,.9])produceTray(b,-3.72,1.28,z,8);
  });
  part('market:stock-wall',()=>{
    for(const x of [-3.5,-1.15,1.15,3.5])box([.12,2.15,.13],[x,1.375,-2.2],1,true);
    for(const y of [.65,1.45])box([7.6,.12,.65],[0,y,-2.15],12,true);
    for(let i=0;i<7;i++){
      const x=-3.15+i*1.05,rng=rngFromString(`${seed}:market:stock:${i}`);
      if(rng()<.5)pot(b,x,1.51,-2.12);else produceTray(b,x,1.54,-2.12,rng()<.5?9:2);
      box([.65,.52,.5],[x,.97,-2.15],1);
      box([.69,.07,.54],[x,1.17,-2.15],12);
    }
    // Hanging goods attach to a wall rail, above the stock shelf.
    box([6.8,.09,.12],[0,2.57,-2.37],1);
    for(const x of [-2.8,-1.4,1.4,2.8]){
      box([.035,.42,.035],[x,2.34,-2.29],6);
      const bundle=node('sphere',[.13],[x,2.23,-2.29],9);
      bundle.modifiers=[{type:'array',mode:'linear',count:3,offset:[0,-.15,0]}];
    }
  });
  part('market:weighing-station',()=>{
    box([.42,.09,.32],[scaleX,1.34,1.1],7);
    box([.045,.5,.045],[scaleX,1.62,1.1],7);
    box([.6,.055,.055],[scaleX,1.87,1.1],7);
    for(const dx of [-.26,.26]){
      box([.02,.3,.02],[scaleX+dx,1.7,1.1],7);
      node('cylinder',[.16,.12,.05],[scaleX+dx,1.54,1.1],7);
    }
  });
  part('market:receiving',()=>{barrel(3.6,-.7);});
  const serving=counterStand([vendorX,1.215,counterZ],[bayW-.4,.17,counterDepth],-1),stock:AssetVec3=[0,.3,-1.15];
  slot('vendor','serve',[...serving],[entranceX,.3,-.15],0);
  slot('customer','browse',[vendorX,.3,2.3],[vendorX,0,4.6],Math.PI);
  return {entrance:[entranceX,0,3.1],workerSlot:'vendor',facades:[],destinations:[
    {id:'shopping-aisle',position:[entranceX,.3,.3]}, {id:'serve',position:[...serving]}, {id:'restock',position:[...stock]},
  ],route:[[...serving],[0,.3,-.15],[...stock]],firstFacing:[0,0,1],lastFacing:[0,0,-1],firstEmote:'emo_counter_idle',lastEmote:'emo_lookaround'};
}

export function frontierGarden(b:FrontierBuilder,d:FrontierPlacePlan['design'],seed:string):WorkplacePlan{
  const {part,box,node,floor,post,awning,slot,barrel}=b,gateX=1.2;
  floor(10,8.6);
  part('farm:earth-and-paths',()=>{
    box([9.5,.018,8.1],[0,.307,0],12);
    box([1.4,.014,7.9],[gateX,.317,0],4);
    for(const z of [1.15,-2.15])box([5.6,.014,.85],[-1.25,.317,z],4);
    floor(2.1,.6,gateX,4.1,.3);
  });
  part('farm:boundary',()=>{
    for(const x of [-4.6,4.6]){
      for(const z of [-4,0,4])post(x,z,1,.3);
      for(const y of [.72,1.1])box([.12,.11,8],[x,y,0],1,true);
    }
    for(const z of [-4,4])for(const [a,c] of (z<0?[[-4.6,4.6]]:[[-4.6,gateX-1],[gateX+1,4.6]])){
      for(const x of [a,c])post(x,z,1,.3);
      for(const y of [.72,1.1])box([c-a,.11,.12],[(a+c)/2,y,z],1,true);
    }
  });
  const benchCenter:AssetVec3=[-2.4,1.225,-3.1],benchSize:AssetVec3=[2.8,.15,.7];
  part('farm:potting-shelter',()=>{
    const shelterWidth=d.homeWidth===9?3.7:3.5,shelterHeight=2.65+(d.marketHeight-3.2)*.6;
    awning('potting',-2.4,shelterWidth,-3.8,-1.5,shelterHeight,shelterHeight,'frame',.35);
    part('farm:potting-bench',()=>counterTop(b,benchCenter,benchSize,12,1));
    for(const x of [-3.5,-1.3])post(x,-3.1,.85,.3);
    box([2.9,.14,.4],[-2.4,1.85,-3.58],12);
    for(const x of [-3.6,-1.2])box([.1,1.65,.1],[x,1.15,-3.62],1,true);
    for(const x of [-3.35,-2.85,-2.35])pot(b,x,1.3,-3.1);
    for(const x of [-3.25,-2.5,-1.75])pot(b,x,1.92,-3.58);
    box([.5,.12,.42],[-1.55,1.365,-3.1],14);
    box([.045,.5,.045],[-1.25,1.6,-3.57],7);
    box([.3,.07,.07],[-1.25,1.35,-3.57],7);
    barrel(-.15,-3.15);
  });
  const plots:GrowingBedPlan[]=[
    {id:'kitchen-bed',crop:'greens',stage:'mature',center:[-2.4,.43,0],size:[3.1,.22,1.15],workPosition:[-2.4,.3,1.15]},
    {id:'rotation-bed',crop:'roots',stage:d.marketBays===2?'seedling':'growing',center:[-2.4,.43,2.6],size:[d.marketBays===2?2.7:3.1,.22,1.35],workPosition:[-2.4,.3,1.15]},
    {id:'climbing-bed',crop:'beans',stage:'mature',center:[3.45,.43,-1.8],size:[1.25,.22,2.7],workPosition:[gateX,.3,-1.8]},
    {id:'succession-bed',crop:'greens',stage:'seedling',center:[3.45,.43,1.8],size:[1.25,.22,1.6],workPosition:[gateX,.3,1.8]},
  ];
  for(const plot of plots)part(`farm:${plot.id}`,()=>{
    const [x,,z]=plot.center,[w,,depth]=plot.size;
    box(plot.size,[...plot.center],14,true);
    for(const dx of [-w/2,w/2])box([.09,.25,depth+.12],[x+dx,.445,z],12,true);
    for(const dz of [-depth/2,depth/2])box([w,.25,.09],[x,.445,z+dz],12,true);
    const rng=rngFromString(`${seed}:farm:${plot.id}`);
    const rows=Math.max(1,Math.floor(depth/.55)),columns=Math.max(2,Math.floor(w/.55));
    for(let row=0;row<rows;row++){
      const zz=z+(row-(rows-1)/2)*.5;
      node('box',[w-.2,.025,.045],[x,.55,zz],12);
      if(plot.crop==='beans')continue;
      const start=x-(columns-1)*.48/2,leafSize=plot.stage==='mature'?.23:plot.stage==='growing'?.16:.09;
      // Alternate crop colour and maturity per bed; equal spacing is intentional agriculture.
      const leaves=node('sphere',[leafSize],[start,.54+leafSize*.7,zz],rng()<.5?8:15);
      leaves.modifiers=[{type:'array',mode:'linear',count:columns,offset:[.48,0,0]}];
      if(plot.stage==='mature'){
        const core=node('sphere',[.13],[start,.72,zz],15);
        core.modifiers=[{type:'array',mode:'linear',count:columns,offset:[.48,0,0]}];
      }else if(plot.crop==='roots'){
        const root=node('cone',[.07,.2],[start,.59,zz],9,[Math.PI,0,0]);
        root.modifiers=[{type:'array',mode:'linear',count:columns,offset:[.48,0,0]}];
      }
    }
    // Label stake lives inside the bed, clear of its working edge.
    box([.05,.45,.05],[x-w/2+.22,.75,z+depth/2-.18],1);
    box([.25,.13,.04],[x-w/2+.22,.94,z+depth/2-.16],6);
  });
  part('farm:bean-trellis',()=>{
    for(const z of [-3.1,-.5])box([.1,2,.1],[3.6,1.45,z],1,true);
    for(const y of [1,1.6,2.35])box([.09,.07,2.7],[3.6,y,-1.8],1);
    for(let i=0;i<5;i++){
      const z=-2.85+i*.5;
      box([.018,1.65,.018],[3.6,1.5,z],6);
      const leaves=node('sphere',[.17],[3.5,.85,z],8);
      leaves.modifiers=[{type:'array',mode:'linear',count:4,offset:[0,.36,0]}];
      const pods=node('box',[.065,.25,.065],[3.34,1.2,z],15);
      pods.modifiers=[{type:'array',mode:'linear',count:2,offset:[0,.58,0]}];
    }
  });
  const tending:AssetVec3=[-2.4,.3,1.15],potting=counterStand(benchCenter,benchSize,1);
  slot('grow','garden',[...tending],[gateX,.3,1.15],Math.PI);
  return {entrance:[gateX,0,4.35],workerSlot:'grow',facades:[],plots,destinations:[
    ...plots.map(p=>({id:p.id,position:[...p.workPosition] as AssetVec3})),{id:'potting',position:[...potting]},{id:'water',position:[gateX,.3,-3.15]},
  ],route:[[...tending],[gateX,.3,1.15],[gateX,.3,-2.15],[...potting]],firstFacing:[0,0,-1],lastFacing:[0,0,-1],firstEmote:'emo_farm_water',lastEmote:'emo_counter_idle'};
}
